import { type StoreApi, type UseBoundStore, create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

import {
  type CompanionSlice,
  companionInitialState,
  createCompanionSlice,
} from "./slices/companionSlice";
import {
  type ConsumerSlice,
  consumerInitialState,
  createConsumerSlice,
} from "./slices/consumerSlice";
import {
  type PricingMapSlice,
  createPricingMapSlice,
  pricingMapInitialState,
} from "./slices/pricingMapSlice";
import {
  type UserSlice,
  createUserSlice,
  userInitialState,
} from "./slices/userSlice";

import type { Practice } from "../graphql/generated-gql";
import {
  type AppointmentSlice,
  createAppointmentsSlice,
} from "./slices/appointmentSlice";
import {
  type BookingSlice,
  bookingInitialState,
  createBookingSlice,
} from "./slices/bookingSlice";
import { SelectedAppointmentTimeEnum } from "./slices/bookingSlice";
import {
  type StorefrontSlice,
  createStorefrontSlice,
  storefrontInitialState,
} from "./slices/storefrontSlice";

const globalInitialState = {
  ...userInitialState,
  ...consumerInitialState,
  ...pricingMapInitialState,
  ...companionInitialState,
  ...storefrontInitialState,
  ...bookingInitialState,
};

const globalInitialStateRelatedToCart = {
  ...companionInitialState,
};

const globalInitialStateRelatedToPostPurchase = {
  ...pricingMapInitialState,
  ...bookingInitialState,
};

/*
When adding more slices, you can use the following pattern:

import {NewState, createNewSlice} from './slices/newSlice'

export const useBoundStore = create<CartState & NewState>()((...a) => ({
    ...createCartSlice(...a),
    ...createNewSlice(...a)
}))

https://www.youtube.com/watch?v=CUjrySBwi5Q
*/

type ResetFunctions = {
  resetAllStores: () => void;
  resetAllCartRelatedStores: () => void;
  resetAllPostPurchaseStores: () => void;
};

export type Store = ResetFunctions &
  UserSlice &
  ConsumerSlice &
  PricingMapSlice &
  CompanionSlice &
  AppointmentSlice &
  BookingSlice &
  StorefrontSlice; // & NewState

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(
  _store: S,
) => {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (const k of Object.keys(store.getState())) {
    // biome-ignore lint/suspicious/noExplicitAny: the any here is intentional
    (store.use as any)[k] = () => store((s) => s[k as keyof typeof s]);
  }

  return store;
};

export const useBoundStoreBase = create<Store>()(
  devtools(
    persist(
      (...a) => ({
        resetAllStores: () => {
          a[0](globalInitialState);
        },
        resetAllCartRelatedStores: () => a[0](globalInitialStateRelatedToCart),
        resetAllPostPurchaseStores: () =>
          a[0](globalInitialStateRelatedToPostPurchase),
        ...createUserSlice(...a),
        ...createConsumerSlice(...a),
        ...createPricingMapSlice(...a),
        ...createCompanionSlice(...a),
        ...createAppointmentsSlice(...a),
        ...createBookingSlice(...a),
        ...createStorefrontSlice(...a),
        // ...createNewSlice(...a)
      }),

      {
        name: "global-store",
        storage: createJSONStorage(() => sessionStorage),
        // TODO: this is currently broken and is not persisting the store at all,
        // commenting it out for now until we can fix it.
        //
        // Omit things that shouldn't be persisted at all here.
        partialize: (state: Store) => {
          return Object.fromEntries(
            Object.entries(state).filter(
              ([key]) =>
                ![
                  "isCreatingNewAppointment",
                  "isEditingExistingAppointment",
                  "isInsuranceModified",
                ].includes(key),
            ),
          ) as Partial<Store>;
          // Note: Date objects are automatically serialized to ISO strings by JSON.stringify
        },
        // Deserialize ISO strings back to Date objects
        onRehydrateStorage: () => (state) => {
          if (state?.selectedAppointmentTime) {
            if (
              typeof state.selectedAppointmentTime === "string" &&
              state.selectedAppointmentTime !== SelectedAppointmentTimeEnum.ASAP
            ) {
              state.selectedAppointmentTime = new Date(
                state.selectedAppointmentTime,
              ) as Date | SelectedAppointmentTimeEnum | null;
            }
          }
        },
      },
    ),
  ),
);

export const useBoundStore = createSelectors(useBoundStoreBase);

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

type PersistentStore = {
  hasSeenLanding: boolean;
  orgId: number;
  practiceId: number | "ALL";
  allPractices: Nullable<Partial<Practice>>[];
  adminId: string;
  setHasSeenLanding: (newValue: boolean) => void;
  setOrgId: (newValue: number) => void;
  setPracticeId: (newValue: number | "ALL") => void;
  setAllPractices: (practices: Nullable<Partial<Practice>>[]) => void;
  setAdminId: (newValue: string) => void;
  enabledDeveloperSettings: Record<string, boolean>;
  setDeveloperSetting: (settingName: string, value: boolean) => void;
};

const usePersistentStoreBase = create<PersistentStore>()(
  devtools(
    persist(
      (set) => ({
        hasSeenLanding: false as boolean,
        orgId: 0,
        practiceId: "ALL",
        allPractices: [],
        adminId: "" as string,
        setHasSeenLanding: (hasSeenLanding) => set({ hasSeenLanding }),
        setOrgId: (orgId) => set({ orgId }),
        setAllPractices: (practices) => set({ allPractices: practices }),
        setPracticeId: (practiceId) => set({ practiceId }),
        setAdminId: (adminId) => set({ adminId }),
        enabledDeveloperSettings: {},
        setDeveloperSetting: (settingName, value) =>
          set((state) => ({
            enabledDeveloperSettings: {
              ...state.enabledDeveloperSettings,
              [settingName]: value,
            },
          })),
      }),
      {
        name: "persistent-store",
      },
    ),
  ),
);

export const usePersistentStore = createSelectors(usePersistentStoreBase);
